import { useState, type ReactNode } from 'react';
import { aClave } from '../nucleo/texto';
import type { ItemAgenda } from '../services/agenda';
import { porHorario } from '../services/agenda';
import { diaLargo } from '../nucleo/fechas';
import { ganador } from '../nucleo/marcador';
import { Boton, ENTRADA, Tarjeta, Titulo, Vacio } from './ui';

/** Búsqueda pública dentro de la categoría o de todas las categorías elegidas. */
export function AgendaBuscable({ items, conCategoria }: { items: ItemAgenda[]; conCategoria: boolean }) {
  const [busqueda, setBusqueda] = useState('');
  const palabras = aClave(busqueda).split('-').filter(Boolean);
  const programados = items.filter((i) => i.dia && i.hora);
  const encontrados = programados.filter((i) => {
    const texto = aClave(`${i.a.texto} ${i.b.texto} ${i.categoria} ${i.fase} ${i.hora} Cancha ${i.cancha ?? ''}`);
    return palabras.every((p) => texto.includes(p));
  });
  const buscando = palabras.length > 0;
  return <>
    <div className="flex flex-col gap-2">
      <label className="text-sm font-semibold" htmlFor="buscar-partido">Buscar un partido</label>
      <div className="flex items-center gap-2">
        <input id="buscar-partido" type="search" className={`${ENTRADA} min-w-0 flex-1`} value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Nombre, apellido, grupo o cancha" />
        {busqueda && <Boton chico tipo="texto" onClick={() => setBusqueda('')}>Limpiar</Boton>}
      </div>
      <p className="text-xs text-(--color-apagado)" role="status">
        {buscando ? `${encontrados.length} de ${programados.length} partidos coinciden con la búsqueda` : `${programados.length} partidos con horario · abrí un día para verlos`}
      </p>
    </div>
    {encontrados.length === 0 ? <Vacio>{buscando ? 'No hay partidos con ese nombre o dato en las categorías elegidas. Probá con un apellido o mirá todas las categorías.' : 'Todavía no hay partidos con horario en las categorías elegidas.'}</Vacio> :
      <AgendaPorDia items={encontrados} conCategoria={conCategoria} plegable={!buscando} />}
  </>;
}

// Los partidos por horario: "en cancha ahora" arriba y después día por día.
// La misma pieza para el público y para el organizador (que le suma botones).

/** Un turno: la hora y la cancha a la izquierda, quiénes juegan a la derecha. */
export function FilaAgenda({ item, conCategoria, children }: { item: ItemAgenda; conCategoria: boolean; children?: ReactNode }) {
  const m = item.marcador;
  const gana = m ? ganador(m) : null;

  const renglon = (lado: 'a' | 'b') => {
    const l = item[lado];
    let numeros: ReactNode = null;
    if (m?.wo) numeros = gana === lado ? <span className="text-xs">W.O.</span> : null;
    else if (m?.sets) {
      numeros = [...m.sets, ...(m.tb ? [m.tb] : [])].map(([x, y], i) => {
        const mio = lado === 'a' ? x : y;
        const suyo = lado === 'a' ? y : x;
        return <span key={i} className={`w-5 text-center ${mio > suyo ? 'font-bold' : ''}`}>{mio}</span>;
      });
    }
    return (
      <div className={`flex justify-between gap-2 text-sm ${gana ? (gana === lado ? 'font-bold' : 'text-(--color-apagado)') : ''}`}>
        <span className={l.seSabe ? '' : 'italic text-(--color-apagado)'}>{l.texto}</span>
        <span className="flex shrink-0 tabular-nums">{numeros}</span>
      </div>
    );
  };

  return (
    <li className="grid grid-cols-[3.25rem_minmax(0,1fr)] gap-2.5 border-t border-(--color-borde) py-2 first:border-t-0">
      <div className="leading-none">
        <span className="font-titulo text-xl text-(--color-acento)">{item.hora ?? (item.dia ? '--:--' : '')}</span>
        <span className="mt-1 block text-[11px] text-(--color-apagado)">{item.cancha === null ? 'sin cancha' : `Cancha ${item.cancha}`}</span>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wide text-(--color-apagado)">
          {conCategoria ? `${item.categoria} · ` : ''}
          {item.fase}
          {item.enJuego && <span className="font-bold text-(--color-vivo)"> · ● en juego</span>}
        </p>
        {renglon('a')}
        {renglon('b')}
      </div>
      {children && <div className="col-span-2 min-w-0">{children}</div>}
    </li>
  );
}

/** Lo que se está jugando en este momento, en todas las categorías. */
export function EnCanchaAhora({ items, extra }: { items: ItemAgenda[]; extra?: (i: ItemAgenda) => ReactNode }) {
  const enJuego = items.filter((i) => i.enJuego).sort(porHorario);
  if (enJuego.length === 0) return null;
  return (
    <Tarjeta className="border-(--color-vivo)">
      <Titulo>
        <span className="text-(--color-vivo)">●</span> En cancha ahora
      </Titulo>
      <ul className="flex flex-col">
        {enJuego.map((i) => (
          <FilaAgenda key={i.id} item={i} conCategoria>
            {extra?.(i)}
          </FilaAgenda>
        ))}
      </ul>
    </Tarjeta>
  );
}

/** Los partidos con horario, un bloque por día. */
export function AgendaPorDia({
  items,
  conCategoria,
  extra,
  plegable,
}: {
  items: ItemAgenda[];
  conCategoria: boolean;
  extra?: (i: ItemAgenda) => ReactNode;
  /** Cada día arranca cerrado y se abre al tocarlo: con 50 partidos por día, la pantalla no se hace eterna. */
  plegable?: boolean;
}) {
  const conHorario = items.filter((i) => i.dia && i.hora).sort(porHorario);
  const dias = [...new Set(conHorario.map((i) => i.dia!))];
  return (
    <>
      {dias.map((dia) => {
        const delDia = conHorario.filter((i) => i.dia === dia);
        const lista = (
          <ul className="flex flex-col">
            {delDia.map((i) => (
              <FilaAgenda key={i.id} item={i} conCategoria={conCategoria}>
                {extra?.(i)}
              </FilaAgenda>
            ))}
          </ul>
        );
        if (!plegable) {
          return (
            <Tarjeta key={dia}>
              <Titulo>{diaLargo(dia)}</Titulo>
              {lista}
            </Tarjeta>
          );
        }
        const porJugar = delDia.filter((i) => !i.jugado && !i.porAbandono).length;
        return (
          <Tarjeta key={dia}>
            {/* <details> nativo: recuerda solo si está abierto mientras la pantalla se actualiza. */}
            <details className="group/dia">
              <summary className="flex min-h-11 cursor-pointer list-none flex-wrap items-baseline gap-2 font-titulo text-2xl uppercase focus-visible:outline-2 focus-visible:outline-(--color-acento)">
                <span aria-hidden="true" className="text-base text-(--color-apagado) transition-transform group-open/dia:rotate-90">▸</span>
                {diaLargo(dia)}
                <span className="font-sans text-xs normal-case tracking-normal text-(--color-apagado)">
                  {delDia.length} partido{delDia.length === 1 ? '' : 's'} · {porJugar} por jugar
                </span>
              </summary>
              <div className="mt-3">{lista}</div>
            </details>
          </Tarjeta>
        );
      })}
    </>
  );
}
